import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Database } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  color?: 'green' | 'blue' | 'purple' | 'orange' | 'pink' | 'indigo';
  lastUpdated?: string;
  dataSource?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  icon: Icon,
  children,
  lastUpdated,
  dataSource
}) => {

  // Fechar modal com ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="border-2 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div 
                className="px-6 py-5 border-b"
                style={{ borderColor: 'var(--border-primary)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="p-3 rounded-xl shadow-sm"
                      style={{
                        backgroundColor: 'var(--bg-tertiary)',
                        opacity: 0.8
                      }}
                    >
                      <div style={{ color: 'var(--primary)' }}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div>
                      <h2 
                        className="text-2xl font-bold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {title}
                      </h2>
                      <div className="flex items-center space-x-4 mt-1">
                        {lastUpdated && (
                          <div 
                            className="flex items-center space-x-1 text-sm"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <Calendar className="h-4 w-4" />
                            <span>Atualizado: {lastUpdated}</span>
                          </div>
                        )}
                        {dataSource && (
                          <div 
                            className="flex items-center space-x-1 text-sm"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <Database className="h-4 w-4" />
                            <span>Fonte: {dataSource}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg transition-colors duration-200"
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {children}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;
